/*
 * Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { webscriptApi } from "@/AlfrescoApi";

// Webscripts are mounted under /alfresco/s/... ; executeWebScript prepends
// `/${servicePath}/` to the scriptPath and `${contextRoot}` as the context.
const CONTEXT_ROOT = "alfresco";
const SERVICE_PATH = "s";

/**
 * Normalize a @alfresco/js-api rejection into an Error with a useful message.
 * @param {*} error - rejection raised by executeWebScript
 * @returns {Error}
 */
function toError(error) {
  if (error instanceof Error) {
    return error;
  }
  const body = error?.response?.body;
  const message =
    body?.detail || body?.message || error?.message || "Webscript call failed";
  return new Error(message);
}

/**
 * Base class for services that communicate with Alfresco webscripts.
 * Delegates HTTP, authentication (ticket) and error handling to the shared
 * `webscriptApi` from @alfresco/js-api, so no manual fetch/auth is needed.
 */
export default class AlfrescoProxyService {
  constructor(serviceName) {
    this.serviceName = serviceName;
  }

  /**
   * GET request to an Alfresco webscript.
   * @param {string} scriptPath - path relative to /alfresco/s/ (ex: "fr/jeci/pristy/...")
   * @param {*} fallback - value returned on error (default: null)
   */
  async get(scriptPath, fallback = null) {
    try {
      return await webscriptApi.executeWebScript(
        "GET",
        scriptPath,
        undefined,
        CONTEXT_ROOT,
        SERVICE_PATH,
      );
    } catch (error) {
      console.warn(`[${this.serviceName}] GET ${scriptPath}:`, error);
      return fallback;
    }
  }

  /**
   * POST request to an Alfresco webscript.
   * @param {string} scriptPath - path relative to /alfresco/s/
   * @param {Object} body
   * @param {Object} options - { throwOnError: false }
   */
  async post(scriptPath, body = {}, { throwOnError = false } = {}) {
    try {
      return await webscriptApi.executeWebScript(
        "POST",
        scriptPath,
        undefined,
        CONTEXT_ROOT,
        SERVICE_PATH,
        body,
      );
    } catch (error) {
      if (throwOnError) {
        console.error(`[${this.serviceName}] POST ${scriptPath}:`, error);
        throw toError(error);
      }
      console.warn(`[${this.serviceName}] POST ${scriptPath}:`, error);
      return null;
    }
  }

  /**
   * DELETE request to an Alfresco webscript.
   * @param {string} scriptPath - path relative to /alfresco/s/
   * @returns {Promise<boolean>} True if deleted successfully
   */
  async delete(scriptPath) {
    try {
      await webscriptApi.executeWebScript(
        "DELETE",
        scriptPath,
        undefined,
        CONTEXT_ROOT,
        SERVICE_PATH,
      );
      return true;
    } catch (error) {
      console.error(`[${this.serviceName}] DELETE ${scriptPath}:`, error);
      throw toError(error);
    }
  }
}
